import { useState, useRef, useEffect, useCallback } from "react";
import { Send, User, X } from "lucide-react";
import { API_URL } from "../../Config";
import Lottie from "lottie-react";

import chatbot from "../../animations/chatbot.json";

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [prompt]);

  const renderMessage = (text) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);

    return parts.map((part, index) => {
      if (part.match(/^https?:\/\//)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            Click here
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const sendPrompt = useCallback(
    async (customPrompt) => {
      const userInput = customPrompt || prompt;
      if (!userInput.trim()) return;

      if (!customPrompt) {
        setMessages((prev) => [...prev, { role: "user", content: userInput }]);
        setPrompt("");
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_URL}/generate-content`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt: userInput,
            history: messages,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to get response");
        }

        if (data.success) {
          const aiMessage = {
            role: "ai",
            content: data.reply,
          };
          setMessages((prev) => [...prev, aiMessage]);
        } else {
          throw new Error(data.reply || "Unknown error occurred");
        }
      } catch (err) {
        console.error("API Error:", err);
        setError(err.message);
        const errorMessage = {
          role: "ai",
          content: "Sorry, I'm having trouble responding. Please try again.",
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
    },
    [prompt, messages]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendPrompt();
      }
    },
    [sendPrompt]
  );

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setError("");
  };

  // Floating chat button when closed
  if (!isOpen) {
    return (
      <div className="fixed bottom-0 -right-5 z-20">
        <button className="" onClick={toggleChat}>
          <Lottie
            animationData={chatbot}
            loop={false}
            autoplay={true}
            className="h-28 w-36"
            rendererSettings={{
              preserveAspectRatio: "xMidYMid slice",
            }}
          />
        </button>
      </div>

      // <button
      //   onClick={toggleChat}
      //   className="fixed bottom-6 right-6 w-14 h-14 bg-red-500 text-white rounded-full shadow-lg hover:bg-gray-800 transition-all duration-200 flex items-center justify-center z-50 hover:scale-110"
      //   aria-label="Open chat"
      // >
      //   <img src="/init_logoWhite.png" alt="Chat" className="w-6 h-6" />
      // </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 w-72 h-[500px] bg-white rounded-xl shadow-2xl flex flex-col z-50 border border-gray-200 animate-in slide-in-from-bottom-5 transition-all duration-300 ease-linear">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-200 dark:bg-gray-800 dark:text-white text-black rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center">
            <img src="/logo512.png" alt="INIT Technology" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">INIT Technology</h3>
            <span className="text-xs opacity-80">Today</span>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* <button
            className="text-xs bg-black hover:bg-black text-white px-2 py-1 rounded transition-colors"
            onClick={() =>
              navigator.share?.({
                title: "INIT Technology",
                url: window.location.href,
              })
            }
          >
            Share
          </button> */}
          <button
            onClick={toggleChat}
            className="w-6 h-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
            aria-label="Close chat"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">
          {error}
        </div>
      )}
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-black p-4">
        {messages.length === 0 && (
          <div className="flex items-start space-x-3 mb-6">
            <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0 mt-8">
              <img src="/logo512.png" alt="INIT Bot" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="bg-white dark:bg-gray-800 dark:text-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-gray-800 dark:text-gray-100 text-sm">
                  Your Init-Bot is here, how can I help you?
                </p>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
                Init-Bot
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex mb-4 ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {/* AI MESSAGE */}
            {msg.role === "ai" && (
              <div className="flex items-end gap-2 max-w-[80%]">
                <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0">
                  <img src="/logo512.png" alt="AI" />
                </div>

                <div className="text-sm px-4 py-3 rounded-2xl bg-white dark:bg-gray-900/0 text-gray-800 dark:text-white border border-gray-500 dark:border-gray-300 rounded-bl-none shadow-sm">
                  <div className="chat-message whitespace-pre-line">
                    {renderMessage(msg.content)}
                  </div>
                </div>
              </div>
            )}

            {/* USER MESSAGE */}
            {msg.role === "user" && (
              <div className="flex items-end gap-2 max-w-[80%]">
                <div className="text-sm px-4 py-3 rounded-2xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-br-none">
                  {msg.content}
                </div>

                <div className="w-8 h-8 border-2 border-gray-600 dark:border-gray-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <User
                    size={21}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Loading Indicator */}
        {loading && (
          <div className="flex mb-4">
            <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0 mr-2">
              <img src="/logo512.png" alt="AI" />
            </div>
            <div className="bg-white dark:bg-gray-900 text-gray-800 border border-gray-500 dark:border-gray-700 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-200 dark:bg-black dark:text-white rounded-b-xl shadow-lg">
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 dark:text-white rounded-2xl px-3 py-2 focus-within:ring-2 focus-within:ring-teal-600 focus-within:bg-white dark:focus-within:bg-gray-700 transition-all">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            className="flex-1 bg-transparent border-none outline-none resize-none text-sm placeholder-gray-500 dark:placeholder-gray-400 max-h-32 py-1 text-black dark:text-white"
            rows="1"
            disabled={loading}
          />

          <button
            onClick={() => sendPrompt()}
            disabled={loading || !prompt.trim()}
            className="bg-teal-600 text-white p-2 rounded-full hover:bg-[#008080] disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;

// import { useState, useRef, useEffect, useCallback } from "react";
// import { Send, User, X } from "lucide-react";
// import { API_URL } from "../../Config";
// import Lottie from "lottie-react";

// import chatbot from "../../animations/chatbot.json";

// const ChatBot = () => {
//   const [messages, setMessages] = useState([]);
//   const [prompt, setPrompt] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [isOpen, setIsOpen] = useState(false);
//   const [error, setError] = useState("");

//   const chatEndRef = useRef(null);
//   const textareaRef = useRef(null);

//   // Auto-scroll to bottom
//   useEffect(() => {
//     chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [messages, loading]);

//   // Auto-resize textarea
//   useEffect(() => {
//     if (textareaRef.current) {
//       textareaRef.current.style.height = "auto";
//       textareaRef.current.style.height = `${Math.min(
//         textareaRef.current.scrollHeight,
//         120
//       )}px`;
//     }
//   }, [prompt]);

//   const sendPrompt = useCallback(
//     async (customPrompt) => {
//       const userInput = customPrompt || prompt;
//       if (!userInput.trim()) return;

//       if (!customPrompt) {
//         setMessages((prev) => [...prev, { role: "user", content: userInput }]);
//         setPrompt("");
//       }

//       setLoading(true);
//       setError("");

//       try {
//         const response = await fetch(`${API_URL}/generate-content`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({
//             prompt: userInput,
//             history: messages,
//           }),
//         });

//         const data = await response.json();

//         if (!response.ok) {
//           throw new Error(data.message || "Failed to get response");
//         }

//         if (data.success) {
//           const aiMessage = {
//             role: "ai",
//             content: data.reply,
//           };
//           setMessages((prev) => [...prev, aiMessage]);
//         } else {
//           throw new Error(data.reply || "Unknown error occurred");
//         }
//       } catch (err) {
//         console.error("API Error:", err);
//         setError(err.message);
//         const errorMessage = {
//           role: "ai",
//           content: "Sorry, I'm having trouble responding. Please try again.",
//         };
//         setMessages((prev) => [...prev, errorMessage]);
//       } finally {
//         setLoading(false);
//       }
//     },
//     [prompt, messages]
//   );

//   const handleKeyDown = useCallback(
//     (e) => {
//       if (e.key === "Enter" && !e.shiftKey) {
//         e.preventDefault();
//         sendPrompt();
//       }
//     },
//     [sendPrompt]
//   );

//   const toggleChat = () => {
//     setIsOpen(!isOpen);
//     setError("");
//   };

//   // Floating chat button when closed
//   if (!isOpen) {
//     return (
//       <div className="fixed bottom-0 -right-5 z-20">
//         <button className="" onClick={toggleChat}>
//           <Lottie
//             animationData={chatbot}
//             loop={false}
//             autoplay={true}
//             className="h-28 w-36"
//             rendererSettings={{
//               preserveAspectRatio: "xMidYMid slice",
//             }}
//           />
//         </button>
//       </div>

//       // <button
//       //   onClick={toggleChat}
//       //   className="fixed bottom-6 right-6 w-14 h-14 bg-red-500 text-white rounded-full shadow-lg hover:bg-gray-800 transition-all duration-200 flex items-center justify-center z-50 hover:scale-110"
//       //   aria-label="Open chat"
//       // >
//       //   <img src="/init_logoWhite.png" alt="Chat" className="w-6 h-6" />
//       // </button>
//     );
//   }

//   return (
//     <div className="fixed bottom-5 right-5 w-72 h-[500px] bg-white rounded-xl shadow-2xl flex flex-col z-50 border border-gray-200 animate-in slide-in-from-bottom-5 transition-all duration-300 ease-linear">
//       {/* Header */}
//       <div className="flex items-center justify-between p-4 bg-gray-200 dark:bg-gray-800 dark:text-white text-black rounded-t-xl">
//         <div className="flex items-center space-x-3">
//           <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center">
//             <img src="/logo512.png" alt="INIT Technology" />
//           </div>
//           <div>
//             <h3 className="font-semibold text-sm">INIT Technology</h3>
//             <span className="text-xs opacity-80">Today</span>
//           </div>
//         </div>
//         <div className="flex items-center space-x-2">
//           <button
//             className="text-xs bg-black hover:bg-black text-white px-2 py-1 rounded transition-colors"
//             onClick={() =>
//               navigator.share?.({
//                 title: "INIT Technology",
//                 url: window.location.href,
//               })
//             }
//           >
//             Share
//           </button>
//           <button
//             onClick={toggleChat}
//             className="w-6 h-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
//             aria-label="Close chat"
//           >
//             <X size={14} />
//           </button>
//         </div>
//       </div>
//       {error && (
//         <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm">
//           {error}
//         </div>
//       )}
//       {/* Messages Container */}
//       <div className="flex-1 overflow-y-auto bg-white dark:bg-black p-4">
//         {messages.length === 0 && (
//           <div className="flex items-start space-x-3 mb-6">
//             <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0 mt-8">
//               <img src="/logo512.png" alt="INIT Bot" />
//             </div>
//             <div className="flex-1 min-w-0">
//               <div className="bg-white dark:bg-gray-800 dark:text-white rounded-2xl rounded-bl-none px-4 py-3 shadow-sm border border-gray-200 dark:border-gray-700">
//                 <p className="text-gray-800 dark:text-gray-100 text-sm">
//                   Your Init-Bot is here, how can I help you?
//                 </p>
//               </div>
//               <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
//                 Init-Bot
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Messages */}
//         {messages.map((msg, index) => (
//           <div
//             key={index}
//             className={`flex mb-4 ${
//               msg.role === "user" ? "justify-end" : "justify-start"
//             }`}
//           >
//             {/* AI MESSAGE */}
//             {msg.role === "ai" && (
//               <div className="flex items-end gap-2 max-w-[80%]">
//                 <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0">
//                   <img src="/logo512.png" alt="AI" />
//                 </div>

//                 <div className="text-sm px-4 py-3 rounded-2xl bg-white dark:bg-gray-900/0 text-gray-800 dark:text-white border border-gray-500 dark:border-gray-300 rounded-bl-none shadow-sm">
//                   {msg.content}
//                 </div>
//               </div>
//             )}

//             {/* USER MESSAGE */}
//             {msg.role === "user" && (
//               <div className="flex items-end gap-2 max-w-[80%]">
//                 <div className="text-sm px-4 py-3 rounded-2xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-br-none">
//                   {msg.content}
//                 </div>

//                 <div className="w-8 h-8 border-2 border-gray-600 dark:border-gray-400 rounded-full flex items-center justify-center flex-shrink-0">
//                   <User
//                     size={21}
//                     className="text-gray-600 dark:text-gray-300"
//                   />
//                 </div>
//               </div>
//             )}
//           </div>
//         ))}

//         {/* Loading Indicator */}
//         {loading && (
//           <div className="flex mb-4">
//             <div className="w-8 h-8 bg-[#358180] rounded-full flex items-center justify-center flex-shrink-0 mr-2">
//               <img src="/logo512.png" alt="AI" />
//             </div>
//             <div className="bg-white dark:bg-gray-900 text-gray-800 border border-gray-500 dark:border-gray-700 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm">
//               <div className="flex space-x-1">
//                 <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
//                 <div
//                   className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
//                   style={{ animationDelay: "0.1s" }}
//                 ></div>
//                 <div
//                   className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
//                   style={{ animationDelay: "0.2s" }}
//                 ></div>
//               </div>
//             </div>
//           </div>
//         )}

//         <div ref={chatEndRef} />
//       </div>

//       {/* Input Area */}
//       <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-200 dark:bg-black dark:text-white rounded-b-xl shadow-lg">
//         <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 dark:text-white rounded-2xl px-3 py-2 focus-within:ring-2 focus-within:ring-teal-600 focus-within:bg-white dark:focus-within:bg-gray-700 transition-all">
//           <textarea
//             ref={textareaRef}
//             value={prompt}
//             onChange={(e) => setPrompt(e.target.value)}
//             onKeyDown={handleKeyDown}
//             placeholder="Type your message..."
//             className="flex-1 bg-transparent border-none outline-none resize-none text-sm placeholder-gray-500 dark:placeholder-gray-400 max-h-32 py-1 text-black dark:text-white"
//             rows="1"
//             disabled={loading}
//           />

//           <button
//             onClick={() => sendPrompt()}
//             disabled={loading || !prompt.trim()}
//             className="bg-teal-600 text-white p-2 rounded-full hover:bg-[#008080] disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
//             aria-label="Send message"
//           >
//             <Send size={16} />
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ChatBot;
